import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Paper,
    Typography,
    Box,
    Grid,
    Divider,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Print as PrintIcon,
    Close as CloseIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import { useApiHandler } from '../hooks/useApiHandler';
import { bookingService } from '../services/bookingService';

const AwbLabels = ({ open, onClose, bookingId, bookingData, autoOpenPrint = false }) => {
    const { showError, showSuccess, showWarning } = useApiHandler();
    const [labelData, setLabelData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && bookingId) {
            loadLabelData();
        }
    }, [open, bookingId]);

    // Auto-trigger print when autoOpenPrint is true and data is loaded
    useEffect(() => {
        if (autoOpenPrint && labelData && open) {
            // Small delay to ensure modal is fully rendered
            setTimeout(() => {
                handlePrint();
            }, 500);
        }
    }, [autoOpenPrint, labelData, open]);

    const loadLabelData = async () => {
        setLoading(true);
        try {
            const response = await bookingService.generateLabels(bookingId);
            if (response.success) {
                setLabelData(response.data);
            } else {
                showError('Failed to load label data');
            }
        } catch (error) {
            console.error('Error loading label data:', error);
            if (error.response?.data?.message) {
                showError(error.response.data.message);
            } else {
                showError('Failed to load label data');
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            showWarning('Please allow popups to print labels');
            return;
        }

        // Write the HTML content
        printWindow.document.write(getFullPrintHTML(labelData));
        printWindow.document.close();
        
        // Wait for images and content to load
        printWindow.addEventListener('load', () => {
            setTimeout(() => {
                printWindow.focus();
                printWindow.print();
            }, 500);
        });

        // Fallback timeout in case load event doesn't fire
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
        }, 2000);
    };

    const handlePrintAlternative = () => {
        // Alternative method: Create a hidden iframe for printing
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.left = '-10000px';
        iframe.style.top = '-10000px';
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(getFullPrintHTML(labelData));
        doc.close();

        setTimeout(() => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            
            // Clean up after printing
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        }, 500);
    };

    const handleClose = () => {
        setLabelData(null);
        onClose();
    };

    const getFullPrintHTML = (data) => {
        const printStyles = `
            <style>
                @page { size: 4in 6in; margin: 0.05in; }
                html, body { margin: 0; padding: 0; width: 100%; -webkit-print-color-adjust: exact; print-color-adjust: exact; font-family: Arial, sans-serif; }
                body { display: block; background: white; }
                /* Page-sized container for each label - 4x6 thermal */
                .label { width: 3.9in; height: 5.9in; overflow: hidden; page-break-after: always; position: relative; padding: 0.02in; box-sizing: border-box; }
                .label:last-child { page-break-after: auto; }
                /* Label content */
                .label-inner { width: 100%; height: 100%; box-sizing: border-box; font-size: 9pt; line-height: 1.2; position: relative; display: flex; flex-direction: column; }
                .label-inner, .label-inner p, .label-inner div, .label-inner span { font-weight: 700; color: #000; }
                /* Logo at top - maximized */
                .top-logo { text-align: center; margin-bottom: 2px; }
                .top-logo img { max-height: 55px; max-width: 90%; width: auto; object-fit: contain; }
                /* Header */
                .header { margin-bottom: 2px; text-align: center; }
                .header h3 { font-size: 11pt; font-weight: 900; margin: 0 0 1px 0; line-height: 1.1; }
                .header p { font-size: 8pt; font-weight: 700; margin: 0; line-height: 1.1; }
                /* Main content box */
                .boxed { border: 2px solid #000; padding: 3px; margin-bottom: 2px; }
                /* Address sections */
                .section { margin-bottom: 2px; padding: 0; }
                .section-title { font-weight: 900; color: #000; margin-bottom: 1px; font-size: 9pt; text-transform: uppercase; }
                .section-content { font-size: 9pt; line-height: 1.25; font-weight: 500; }
                /* Info box */
                .info-box { border: 2px solid #000; padding: 2px; margin-bottom: 2px; text-align: center; background: white; }
                .info-box p { font-size: 8pt; font-weight: 900; margin: 1px 0; line-height: 1.2; }
                .info-box .large { font-size: 9pt; font-weight: 900; }
                /* Tracking numbers */
                .tracking-box { border: 3px solid #000; padding: 3px; margin: 2px 0; text-align: center; background: white; }
                .tracking-box p { font-size: 8pt; font-weight: 900; margin: 1px 0; }
                .tracking-box h3 { font-size: 14pt; font-weight: 900; margin: 2px 0; letter-spacing: 0.5px; }
                /* Barcode section */
                .barcode-section { text-align: center; margin-top: 3px; padding: 2px 0; }
                .barcode-section img { width: 90%; height: auto; max-height: 65px; }
                .barcode-section p { font-size: 11pt; font-weight: 900; margin: 2px 0; letter-spacing: 0.5px; }
                /* Package number at bottom */
                .package-number { text-align: center; padding: 3px 0; font-size: 9pt; font-weight: 900; border-top: 2px solid #000; margin-top: auto; }
                /* Meta info */
                .meta-row { display: flex; justify-content: space-between; padding: 2px 0; font-size: 7pt; font-weight: 700; }
                /* Tax info compact */
                .tax-info { font-size: 7pt; line-height: 1.1; }
                /* Ensure content fits */
                .label, .label-inner, .section { page-break-inside: avoid; }
                /* Make all text darker for thermal printing */
                * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            </style>
        `;

        let labelsHTML = '';
        for (let i = 1; i <= data.no_of_pack; i++) {
            labelsHTML += `
                <div class="label">
                    <div class="label-inner">
                        <!-- Logo at Top - Maximized -->
                        <div class="top-logo">
                            <img src="/admin/assets/images/logo.png" alt="SunEx Courier Logo" onerror="this.style.display='none'" />
                        </div>

                        <!-- Header Section -->
                        <div class="header">
                            <h3>${data.origin} To ${data.destination}</h3>
                            <p>DATE: ${new Date(data.booking_date).toLocaleDateString('en-GB')}</p>
                        </div>

                        <!-- Main Tracking Number -->
                        <div class="tracking-box">
                            <p>AWB TRACKING NO</p>
                            <h3>${data.awb_no}</h3>
                            <p>SUB NO: ${data.awb_no}-${i}</p>
                        </div>

                        <!-- Shipper Section -->
                        <div class="boxed">
                            <div class="section">
                                <div class="section-title">FROM:</div>
                                <div class="section-content">
                                    <strong>${data.shipper.name}</strong><br/>
                                    ${data.shipper.company ? data.shipper.company + '<br/>' : ''}
                                    ${data.shipper.address}<br/>
                                    ${data.shipper.city}, ${data.shipper.state} - ${data.shipper.pincode}<br/>
                                    <strong>Ph: ${data.shipper.mobile}</strong>
                                    ${data.shipper.gst_no ? '<br/><strong>GST: ' + data.shipper.gst_no + '</strong>' : ''}
                                </div>
                            </div>
                        </div>

                        <!-- Recipient Section -->
                        <div class="boxed">
                            <div class="section">
                                <div class="section-title">TO:</div>
                                <div class="section-content" style="font-weight: 900;">
                                    <strong>${data.recipient.name}</strong><br/>
                                    ${data.recipient.company ? data.recipient.company + '<br/>' : ''}
                                    ${data.recipient.address}<br/>
                                    ${data.recipient.city}, ${data.recipient.state} - ${data.recipient.pincode}<br/>
                                    <strong>Ph: ${data.recipient.mobile}</strong>
                                    ${data.recipient.gst_no ? '<br/><strong>GST: ' + data.recipient.gst_no + '</strong>' : ''}
                                </div>
                            </div>
                        </div>

                        <!-- Package Info -->
                        <div class="info-box">
                            <p><strong>PRODUCT:</strong> ${data.package_name}</p>
                            <p><strong>PCS:</strong> ${data.no_of_pack} | <strong>WT:</strong> ${data.charged_weight} kg</p>
                            ${(i === 1 && data.payment_info) ? `
                                <p style="margin-top: 2px;"><strong>${data.payment_info.payment_type === 'paid' ? 'PAID' : 'TO PAY'}: ₹${data.payment_info.grand_total}</strong></p>
                                <div class="tax-info">
                                    <span>Base: ₹${data.payment_info.total_amount}</span> |
                                    <span>SGST: ₹${data.payment_info.sgst}</span> |
                                    <span>CGST: ₹${data.payment_info.cgst}</span>
                                </div>
                            ` : ''}
                        </div>

                        <!-- Barcode -->
                        <div class="barcode-section">
                            <img src="data:image/png;base64,${data.barcode_data}"
                                 alt="Barcode" />
                            <p>${data.awb_no}</p>
                        </div>

                        <!-- Meta Info -->
                        <div class="meta-row">
                            <span>REF: ${data.ref_no || 'N/A'}</span>
                            <span>${data.current_date || new Date(data.booking_date).toLocaleDateString('en-GB')}</span>
                        </div>

                        <!-- Package Number -->
                        <div class="package-number">
                            PKG ${i} of ${data.no_of_pack}
                        </div>
                    </div>
                </div>
            `;
        }

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <title>AWB Labels - ${data.awb_no}</title>
                ${printStyles}
            </head>
            <body>
                ${labelsHTML}
            </body>
            </html>
        `;
    };

    const renderSingleLabel = (packageNumber) => (
        <Paper
            key={packageNumber}
            sx={{
                width: '4in',
                minHeight: '6in',
                p: 1,
                mb: 2,
                border: '2px solid #000',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible',
                '@media print': {
                    height: '6in',
                    margin: 0,
                    border: '2px solid #000',
                    pageBreakAfter: packageNumber < labelData.no_of_pack ? 'always' : 'auto'
                }
            }}
        >
            {/* Content wrapper */}
            <Box sx={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}>
                {/* Logo at Top - Maximized */}
                <Box sx={{ textAlign: 'center', mb: 0.5 }}>
                    <img
                        src="/admin/assets/images/logo.png"
                        alt="SunEx Courier Logo"
                        onError={(e)=>{ e.target.style.display='none'; }}
                        style={{ maxHeight: '55px', maxWidth: '90%', width: 'auto', objectFit: 'contain' }}
                    />
                </Box>

                {/* Header Section */}
                <Box sx={{ textAlign: 'center', mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight={900} sx={{ fontSize: '11pt', lineHeight: 1.1, mb: 0.2 }}>
                        {labelData.origin} To {labelData.destination}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: '8pt', lineHeight: 1.1 }}>
                        DATE: {new Date(labelData.booking_date).toLocaleDateString('en-GB')}
                    </Typography>
                </Box>

                {/* Main Tracking Number */}
                <Box sx={{ border: '3px solid #000', p: 0.5, mb: 0.5, textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight={900} sx={{ fontSize: '8pt' }}>
                        AWB TRACKING NO
                    </Typography>
                    <Typography variant="h6" fontWeight={900} sx={{ fontSize: '14pt', letterSpacing: 0.5, my: 0.3 }}>
                        {labelData.awb_no}
                    </Typography>
                    <Typography variant="caption" fontWeight={900} sx={{ fontSize: '8pt' }}>
                        SUB NO: {labelData.awb_no}-{packageNumber}
                    </Typography>
                </Box>

                {/* Shipper Section */}
                <Box sx={{ border: '2px solid #000', p: 0.4, mb: 0.3 }}>
                    <Typography variant="caption" fontWeight={900} sx={{ fontSize: '9pt', textTransform: 'uppercase', mb: 0.2 }}>
                        FROM:
                    </Typography>
                    <Typography variant="caption" component="div" fontWeight={500} sx={{ fontSize: '9pt', lineHeight: 1.25 }}>
                        <strong>{labelData.shipper.name}</strong><br/>
                        {labelData.shipper.company && <>{labelData.shipper.company}<br/></>}
                        {labelData.shipper.address}<br/>
                        {labelData.shipper.city}, {labelData.shipper.state} - {labelData.shipper.pincode}<br/>
                        <strong>Ph: {labelData.shipper.mobile}</strong>
                        {labelData.shipper.gst_no && <><br/><strong>GST: {labelData.shipper.gst_no}</strong></>}
                    </Typography>
                </Box>

                {/* Recipient Section */}
                <Box sx={{ border: '2px solid #000', p: 0.4, mb: 0.3 }}>
                    <Typography variant="caption" fontWeight={900} sx={{ fontSize: '9pt', textTransform: 'uppercase', mb: 0.2 }}>
                        TO:
                    </Typography>
                    <Typography variant="caption" component="div" fontWeight={500} sx={{ fontSize: '9pt', lineHeight: 1.25 }}>
                        <strong>{labelData.recipient.name}</strong><br/>
                        {labelData.recipient.company && <>{labelData.recipient.company}<br/></>}
                        {labelData.recipient.address}<br/>
                        {labelData.recipient.city}, {labelData.recipient.state} - {labelData.recipient.pincode}<br/>
                        <strong>Ph: {labelData.recipient.mobile}</strong>
                        {labelData.recipient.gst_no && <><br/><strong>GST: {labelData.recipient.gst_no}</strong></>}
                    </Typography>
                </Box>

                {/* Package Info */}
                <Box sx={{ border: '2px solid #000', p: 0.3, mb: 0.3, textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight={900} sx={{ fontSize: '8pt', lineHeight: 1.2 }}>
                        <strong>PRODUCT:</strong> {labelData.package_name}
                    </Typography><br/>
                    <Typography variant="caption" fontWeight={900} sx={{ fontSize: '8pt', lineHeight: 1.2 }}>
                        <strong>PCS:</strong> {labelData.no_of_pack} | <strong>WT:</strong> {labelData.charged_weight} kg
                    </Typography>
                    {(packageNumber === 1 && labelData.payment_info) && (
                        <>
                            <br/>
                            <Typography variant="caption" fontWeight={900} sx={{ fontSize: '8pt', mt: 0.3 }}>
                                <strong>{labelData.payment_info.payment_type === 'paid' ? 'PAID' : 'TO PAY'}: ₹{labelData.payment_info.grand_total}</strong>
                            </Typography><br/>
                            <Typography variant="caption" sx={{ fontSize: '7pt', lineHeight: 1.1 }}>
                                Base: ₹{labelData.payment_info.total_amount} | SGST: ₹{labelData.payment_info.sgst} | CGST: ₹{labelData.payment_info.cgst}
                            </Typography>
                        </>
                    )}
                </Box>

                {/* Barcode Section */}
                <Box sx={{ textAlign: 'center', mt: 0.3 }}>
                    <img
                        src={`data:image/png;base64,${labelData.barcode_data}`}
                        alt={`Barcode for ${labelData.awb_no}`}
                        style={{ maxHeight: '65px', width: '90%', height: 'auto' }}
                    />
                    <Typography variant="body2" fontWeight={900} sx={{ fontSize: '11pt', letterSpacing: 0.5, mt: 0.3 }}>
                        {labelData.awb_no}
                    </Typography>
                </Box>

                {/* Meta Info */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 0.3, mt: 0.3 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: '7pt' }}>
                        REF: {labelData.ref_no || 'N/A'}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: '7pt' }}>
                        {labelData.current_date}
                    </Typography>
                </Box>
            </Box>

            {/* Package Number pinned at bottom */}
            <Box sx={{ textAlign: 'center', borderTop: '2px solid #000', pt: 0.5, mt: 'auto' }}>
                <Typography variant="body2" fontWeight={900} sx={{ fontSize: '9pt' }}>
                    PKG {packageNumber} of {labelData.no_of_pack}
                </Typography>
            </Box>
        </Paper>
    );

    if (loading) {
        return (
            <Dialog open={open} maxWidth="md" fullWidth>
                <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        Generating labels...
                    </Typography>
                </DialogContent>
            </Dialog>
        );
    }

    if (!labelData) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogContent>
                    <Alert severity="error">
                        Failed to load label data. Please try again.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <>
            {/* Print Styles */}
            <style jsx global>{`
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    #printable-labels, #printable-labels * {
                        visibility: visible;
                    }
                    #printable-labels {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                    .MuiDialog-root {
                        display: none !important;
                    }
                }
            `}</style>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth={false}
                scroll="paper"
                PaperProps={{
                    sx: {
                        width: 'auto',
                        maxWidth: '95vw',
                        maxHeight: '95vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <DialogTitle>
                    AWB Labels - {labelData.awb_no}
                    <Typography variant="body2" color="text.secondary">
                        {labelData.no_of_pack} package(s) to print
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    p: 2,
                    overflow: 'auto',
                    flexGrow: 1
                }}>
                    <div id="printable-labels" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {Array.from({ length: labelData.no_of_pack }, (_, index) =>
                            renderSingleLabel(index + 1)
                        )}
                    </div>
                </DialogContent>

                <DialogActions>
                    <Button 
                        onClick={handleClose}
                        startIcon={<CloseIcon />}
                    >
                        Close
                    </Button>
                    <Button 
                        variant="outlined"
                        onClick={handlePrintAlternative}
                        startIcon={<PrintIcon />}
                        sx={{ mr: 1 }}
                    >
                        Print (Alternative)
                    </Button>
                    <Button 
                        variant="contained"
                        onClick={handlePrint}
                        startIcon={<PrintIcon />}
                    >
                        Print Labels
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AwbLabels; 